import { DatesRangeValue } from '@mantine/dates';
import { Group } from 'src/design/atoms/Group';
import { Progress } from 'src/design/atoms/Progress';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import React from 'react';
import { AdminStatsOverviewDto } from 'src/api/FiretrolApiClient';
import {
  FEEDBACK_RATING_DOWN,
  FEEDBACK_RATING_UP,
  feedbackReasonLabel,
} from 'src/app/chat/messageFeedback';
import { AnchorLink } from 'src/design/molecules/AnchorLink';
import { StatSectionCard } from 'src/design/molecules/StatSectionCard';
import {
  FeedbackRating,
  feedbackTabHref,
} from 'src/routes/Settings/AIAssistant/aiAssistantSearchParams';
import { formatNumber, formatPercent } from 'src/utils';

export interface IAnswerQualityCardProps {
  overview: AdminStatsOverviewDto | null;
  loading: boolean;
  dateRange: DatesRangeValue;
  /** Scopes every Feedback link to one user. */
  userId?: number;
}

/** Share of rated replies voted helpful, floored to a whole percent so a single bad vote never displays as 100%; null when nothing was rated. */
export const helpfulRate = (up: number, down: number): number | null =>
  up + down > 0 ? Math.floor((up * 100) / (up + down)) / 100 : null;

const voteCountLabel = (count: number, kind: string) =>
  `${formatNumber(count)} ${kind} ${count === 1 ? 'vote' : 'votes'}`;

export const AnswerQualityCard: React.FC<IAnswerQualityCardProps> = ({
  overview,
  loading,
  dateRange,
  userId,
}) => {
  const up = overview?.positiveFeedbackCount ?? 0;
  const down = overview?.negativeFeedbackCount ?? 0;
  const rate = helpfulRate(up, down);
  const reasonCounts = overview?.feedbackReasonCounts ?? [];
  const href = (rating?: FeedbackRating, reasons: string[] = []) =>
    feedbackTabHref({ dateRange, userId, rating, reasons });

  return (
    <StatSectionCard
      title="Answer quality"
      value={rate === null ? '—' : formatPercent(rate, { fractionDigits: 0 })}
      valueLabel="helpful"
      hint={rate === null ? 'No ratings in this range' : undefined}
      loading={loading}
      action={
        <AnchorLink to={href()} size="sm">
          Review feedback →
        </AnchorLink>
      }
      metrics={[
        {
          label: 'helpful',
          value: (
            <AnchorLink
              to={href(FEEDBACK_RATING_UP)}
              fw={600}
              size="sm"
              aria-label={voteCountLabel(up, 'helpful')}
            >
              {formatNumber(up)}
            </AnchorLink>
          ),
        },
        {
          label: 'not helpful',
          value: (
            <AnchorLink
              to={href(FEEDBACK_RATING_DOWN)}
              fw={600}
              size="sm"
              aria-label={voteCountLabel(down, 'not helpful')}
            >
              {formatNumber(down)}
            </AnchorLink>
          ),
        },
        { label: 'rated', value: formatNumber(up + down) },
      ]}
    >
      {!loading && down > 0 && reasonCounts.length > 0 && (
        <Stack gap="xs" data-testid="feedback-reason-list">
          <Text size="xs" fw={600} tt="uppercase" c="gray.7">
            Not helpful, by reason
          </Text>
          {reasonCounts.map(({ reason, count }) => {
            const label = feedbackReasonLabel(reason);
            return (
              <Stack key={reason} gap={4}>
                <Group justify="space-between" gap="sm" wrap="nowrap">
                  <AnchorLink
                    to={href(FEEDBACK_RATING_DOWN, [reason])}
                    size="sm"
                  >
                    {label}
                  </AnchorLink>
                  <Text size="sm" fw={600} c="bright">
                    {formatNumber(count)}
                  </Text>
                </Group>
                <Progress
                  value={(count / down) * 100}
                  size="sm"
                  aria-label={`${label}: ${count} of ${down} not helpful`}
                />
              </Stack>
            );
          })}
        </Stack>
      )}
    </StatSectionCard>
  );
};
